import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useTenantCreationRequest } from '@/hooks/use-tenant-creation-request';
import * as dtos from '@/lib/dtos';
import { OnboardingAccountActions } from '@/pages/onboarding/onboarding-account-actions';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;

// Hand-written fetch matching the dtos/hook pattern used elsewhere in this
// flow (e.g. use-patch-tenant-request.ts) — NOT the generated OpenAPI
// client, per T7b's precedent (the client can't emit a typed shape for
// this endpoint's plain-interface DTOs).
async function completeOnboarding(
  slug: string
): Promise<dtos.TenantCreationRequest> {
  const response = await fetch(
    `${API_BASE_URL}/tenant-creation-requests/${slug}/complete-onboarding`,
    {
      method: 'POST',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to complete onboarding');
  }

  return response.json();
}

export default function OnboardingCompletePage() {
  const creationRequest = useTenantCreationRequest();
  const queryClient = useQueryClient();
  const hasTriggeredRef = useRef(false);

  const completeMutation = useMutation({
    mutationFn: () => completeOnboarding(creationRequest.slug),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['tenant-creation-requests', creationRequest.slug],
      });
    },
    onError: (error: Error) => {
      toast.error('Error', { description: error.message });
    },
  });

  useEffect(() => {
    // Idempotent: only call the endpoint once, and only when the TCR hasn't
    // already transitioned — revisiting this step after completion must not
    // re-trigger the transition (backend also rejects a second call, but we
    // avoid firing it at all here).
    if (hasTriggeredRef.current) {
      return;
    }
    if (creationRequest.stage === 'waiting-for-info') {
      hasTriggeredRef.current = true;
      completeMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationRequest.stage]);

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center p-6">
      <OnboardingAccountActions className="absolute left-4 top-4" />
      <Card className="max-w-md">
        <CardHeader className="items-center text-center">
          <CheckCircle2 className="mb-2 h-12 w-12 text-primary" />
          <CardTitle>Seu app está sendo construído!</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 text-center text-muted-foreground">
          <p>
            Nosso time já recebeu todas as informações e vai entrar em contato
            em breve para dar os próximos passos.
          </p>
          {creationRequest.tenantSlug && (
            <Button asChild>
              <Link to={`/${creationRequest.tenantSlug}/dashboard`}>
                Ir para o dashboard
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
